import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import type { Variant } from "@mui/material/styles/createTypography";

import { MUIAccordion, MUIAccordionDetails, MUIAccordionSummary } from "shared/atoms/MUIAccordion";

interface Props {
  children: React.ReactNode;
  className?: string;
  disableActions?: boolean;
  fieldItem: { id: string };
  hasMoreThanOne: boolean;
  index: number;
  label: string;
  variant?: Variant;
  wrapperComponent?: "accordion";
  onRemove?: () => void;
}

export const SortableItem: React.FC<Props> = ({
  children,
  className,
  disableActions = false,
  fieldItem,
  hasMoreThanOne,
  label,
  variant = "h6",
  wrapperComponent,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: fieldItem.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getControls = () => (
    <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
      {onRemove && (
        <IconButton
          disabled={disableActions}
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          color="error"
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      )}

      {hasMoreThanOne && (
        <IconButton
          disabled={disableActions}
          {...attributes}
          {...listeners}
          size="small"
          sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
        >
          <DragIndicatorIcon />
        </IconButton>
      )}
    </Box>
  );

  if (wrapperComponent === "accordion") {
    return (
      <div ref={setNodeRef} className={className} style={style}>
        <MUIAccordion defaultExpanded sx={{ mb: 2 }}>
          <MUIAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Typography variant={variant}>{label}</Typography>
              {getControls()}
            </Box>
          </MUIAccordionSummary>
          <MUIAccordionDetails>{children}</MUIAccordionDetails>
        </MUIAccordion>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} className={className} style={style}>
      <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%", mb: 2 }}>
        {children}
        {getControls()}
      </Box>
    </div>
  );
};
